import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { cn } from "../lib/cn";
import { microcopy } from "../lib/microcopy";

export const chartPalette = ["#92722a", "#3788a0", "#c08153", "#dabc70", "#2e5b6f", "#94876e", "#b36e45", "#52a4ba"];

const baseTextStyle = { fontFamily: "Inter Variable, Noto Kufi Arabic Variable, system-ui, sans-serif", color: "#463f34" };

type Axis = Record<string, unknown>;
const mapAxes = (axis: unknown, f: (a: Axis) => Axis) => (Array.isArray(axis) ? axis.map((a) => f(a as Axis)) : axis ? f(axis as Axis) : axis);

const SIDE: Record<string, string> = { right: "left", left: "right", insideRight: "insideLeft", insideLeft: "insideRight" };
function flipSeries(s: Axis): Axis {
  if (s.type !== "bar") return s;
  const label = s.label as Axis | undefined;
  const flipRadius = (d: unknown) => {
    const item = d as { itemStyle?: { borderRadius?: number[] } } | null;
    const r = item?.itemStyle?.borderRadius;
    return item && Array.isArray(r) && r.length === 4 ? { ...item, itemStyle: { ...item.itemStyle, borderRadius: [r[1], r[0], r[3], r[2]] } } : d;
  };
  return {
    ...s,
    ...(label && typeof label.position === "string" && SIDE[label.position] ? { label: { ...label, position: SIDE[label.position] } } : {}),
    ...(Array.isArray(s.data) ? { data: (s.data as unknown[]).map(flipRadius) } : {}),
  };
}

/** Mirrors a cartesian chart for right-to-left reading: the category or value axis runs from the right, the value labels sit on the right. */
function mirrored(option: EChartsOption): EChartsOption {
  const legend = option.legend as Axis | undefined;
  return {
    ...option,
    ...(option.xAxis ? { xAxis: mapAxes(option.xAxis, (a) => ({ ...a, inverse: !a.inverse })) as EChartsOption["xAxis"] } : {}),
    ...(option.yAxis ? { yAxis: mapAxes(option.yAxis, (a) => ({ ...a, position: a.position === "right" ? "left" : "right" })) as EChartsOption["yAxis"] } : {}),
    ...(Array.isArray(option.series) ? { series: option.series.map((s) => flipSeries(s as Axis)) as EChartsOption["series"] } : {}),
    ...(legend && !Array.isArray(legend) ? { legend: { ...legend, align: "right", ...(legend.left !== undefined || legend.right !== undefined ? { left: legend.right, right: legend.left } : {}) } as EChartsOption["legend"] } : {}),
  };
}

export function EChart({ option, className, height = 280, rtl, onClick }: { option: EChartsOption; className?: string; height?: number | string; rtl?: boolean; onClick?: (params: unknown) => void }) {
  const isRtl = rtl ?? (typeof document !== "undefined" && document.documentElement.dir === "rtl");
  const merged = useMemo<EChartsOption>(
    () => {
      const base: EChartsOption = {
        color: chartPalette,
        textStyle: baseTextStyle,
        animationDuration: 900,
        animationEasing: "cubicOut",
        grid: { left: 12, right: 12, top: 28, bottom: 12, containLabel: true },
        tooltip: { trigger: "item", backgroundColor: "#1a1a1a", borderWidth: 0, textStyle: { color: "#fff", fontSize: 12 }, ...((option.tooltip as object) ?? {}) },
        ...option,
      };
      return isRtl ? mirrored(base) : base;
    },
    [option, isRtl],
  );
  return (
    <div className={cn("w-full", className)} dir={isRtl ? "rtl" : "ltr"}>
      <ReactECharts option={merged} style={{ height, width: "100%" }} notMerge lazyUpdate onEvents={onClick ? { click: onClick } : undefined} />
    </div>
  );
}

/** Horizontal bar chart of market shares with the merging parties highlighted. */
export function MarketShareBars({ shares, merging = [], height = 260, unit = "%" }: { shares: Array<{ name: string; value: number }>; merging?: string[]; height?: number; unit?: string }) {
  const sorted = [...shares].sort((a, b) => a.value - b.value);
  const option: EChartsOption = {
    // Headroom past the largest bar keeps its value label inside the chart in both reading directions.
    xAxis: { type: "value", max: ({ max }: { max: number }) => Math.ceil((max * 1.15) / 5) * 5, axisLabel: { formatter: `{value}${unit}` }, splitLine: { lineStyle: { color: "#f0ebe1" } } },
    yAxis: { type: "category", data: sorted.map((s) => s.name), axisLine: { show: false }, axisTick: { show: false } },
    series: [
      {
        type: "bar",
        data: sorted.map((s) => ({ value: s.value, itemStyle: { color: merging.includes(s.name) ? "#92722a" : "#cfc5b1", borderRadius: [0, 6, 6, 0] } })),
        barMaxWidth: 22,
        label: { show: true, position: "right", formatter: `{c}${unit}`, color: "#463f34", fontSize: 11 },
      },
    ],
    tooltip: { trigger: "item", formatter: (p: { name: string; value: number }) => `${p.name}: <b>${p.value}${unit}</b>` } as EChartsOption["tooltip"],
  };
  return <EChart option={option} height={height} />;
}

/**
 * HHI before and after the transaction. The band marks come from the published calculation method (GET /api/analysis/method) as a prop –
 * the chart holds no threshold of its own; without them it draws the two bars only.
 */
export function HhiComparison({ pre, post, height = 220, bands }: { pre: number; post: number; height?: number; bands?: { moderate: number; high: number } }) {
  const tone = !bands ? "#92722a" : post >= bands.high ? "#c0392b" : post >= bands.moderate ? "#d08a2c" : "#2f8f5b";
  const option: EChartsOption = {
    xAxis: { type: "category", data: [microcopy("hhiPre"), microcopy("hhiPost")], axisLine: { show: false }, axisTick: { show: false } },
    yAxis: { type: "value", max: Math.max(bands ? bands.high * 1.5 : 0, post + 300), splitLine: { lineStyle: { color: "#f0ebe1" } } },
    series: [
      {
        type: "bar",
        data: [
          { value: pre, itemStyle: { color: "#cfc5b1", borderRadius: [8, 8, 0, 0] } },
          { value: post, itemStyle: { color: tone, borderRadius: [8, 8, 0, 0] } },
        ],
        barMaxWidth: 64,
        label: { show: true, position: "top", color: "#1a1a1a", fontWeight: 600 },
        markLine: bands
          ? {
              symbol: "none",
              lineStyle: { type: "dashed", color: "#94876e" },
              label: { position: "insideEndTop", fontSize: 10, color: "#766b56" },
              data: [
                { yAxis: bands.moderate, name: microcopy("moderatelyConcentrated") },
                { yAxis: bands.high, name: microcopy("highlyConcentrated") },
              ],
            }
          : undefined,
      },
    ],
  };
  return <EChart option={option} height={height} />;
}

export function FunnelChart({ stages, height = 260 }: { stages: Array<{ name: string; value: number }>; height?: number }) {
  const option: EChartsOption = {
    series: [
      {
        type: "funnel",
        left: "5%",
        width: "90%",
        top: 10,
        bottom: 10,
        minSize: "12%",
        sort: "none",
        gap: 4,
        label: { position: "inside", formatter: "{b}\n{c}", color: "#fff", fontSize: 11 },
        itemStyle: { borderColor: "#fff", borderWidth: 2, borderRadius: 6 },
        data: stages,
      },
    ],
  };
  return <EChart option={option} height={height} />;
}

/** A heatmap of counts; given `onCellClick`, a cell opens what it counts (its x and y index). */
export function Heatmap({ xLabels, yLabels, values, height = 260, colorRange = ["#eef8f1", "#d08a2c", "#c0392b"], onCellClick }: { xLabels: string[]; yLabels: string[]; values: Array<[number, number, number]>; height?: number; colorRange?: string[]; onCellClick?: (x: number, y: number) => void }) {
  const max = Math.max(1, ...values.map((v) => v[2]));
  const option: EChartsOption = {
    grid: { left: 12, right: 12, top: 12, bottom: 56, containLabel: true },
    xAxis: { type: "category", data: xLabels, position: "top", splitArea: { show: true }, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { interval: 0, fontSize: 10, width: 56, overflow: "break", lineHeight: 12 } },
    yAxis: { type: "category", data: yLabels, splitArea: { show: true }, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { fontSize: 11, width: 150, overflow: "truncate" } },
    visualMap: { min: 0, max, calculable: false, orient: "horizontal", left: "center", bottom: 0, inRange: { color: colorRange }, textStyle: { fontSize: 10 } },
    series: [{ type: "heatmap", data: values, label: { show: true, fontSize: 11 }, itemStyle: { borderColor: "#fff", borderWidth: 2, borderRadius: 6 }, emphasis: { itemStyle: { shadowBlur: 8, shadowColor: "rgba(0,0,0,0.2)" } }, cursor: onCellClick ? "pointer" : "default" }],
  };
  const click = onCellClick ? (p: unknown) => { const v = (p as { value?: unknown }).value; if (Array.isArray(v) && typeof v[0] === "number" && typeof v[1] === "number") onCellClick(v[0], v[1]); } : undefined;
  return <EChart option={option} height={height} onClick={click} />;
}

export function TrendLine({ series, categories, height = 240, area = true }: { series: Array<{ name: string; data: number[] }>; categories: string[]; height?: number; area?: boolean }) {
  const option: EChartsOption = {
    tooltip: { trigger: "axis" },
    legend: { top: 0, type: "scroll", itemWidth: 12, itemHeight: 8, textStyle: { fontSize: 11 } },
    grid: { left: 12, right: 12, top: 44, bottom: 8, containLabel: true },
    xAxis: { type: "category", data: categories, boundaryGap: false, axisLine: { lineStyle: { color: "#e3dccd" } } },
    yAxis: { type: "value", minInterval: 1, splitLine: { lineStyle: { color: "#f0ebe1" } } },
    series: series.map((s) => ({ name: s.name, type: "line", smooth: true, symbol: "circle", symbolSize: 6, data: s.data, areaStyle: area ? { opacity: 0.12 } : undefined, lineStyle: { width: 2.5 } })),
  };
  return <EChart option={option} height={height} />;
}

/** Stacked columns per period — readable for many categories where overlapping lines are not. */
export function StackedBars({ series, categories, height = 260 }: { series: Array<{ name: string; data: number[] }>; categories: string[]; height?: number }) {
  const option: EChartsOption = {
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { top: 0, type: "scroll", itemWidth: 12, itemHeight: 8, textStyle: { fontSize: 11 } },
    grid: { left: 12, right: 12, top: 44, bottom: 8, containLabel: true },
    xAxis: { type: "category", data: categories, axisLine: { lineStyle: { color: "#e3dccd" } }, axisTick: { show: false } },
    yAxis: { type: "value", minInterval: 1, splitLine: { lineStyle: { color: "#f0ebe1" } } },
    series: series.map((s) => ({ name: s.name, type: "bar", stack: "total", barMaxWidth: 36, data: s.data, itemStyle: { borderRadius: 2 } })),
  };
  return <EChart option={option} height={height} />;
}

export function DonutChart({ data, height = 220, center, legend = "bottom" }: { data: Array<{ name: string; value: number }>; height?: number; center?: string; legend?: "bottom" | "right" | "none" }) {
  const option: EChartsOption = {
    legend: legend === "none" ? { show: false } : legend === "right"
      ? { orient: "vertical", right: 0, top: "middle", type: "scroll", itemWidth: 12, itemHeight: 8, textStyle: { fontSize: 11, width: 130, overflow: "truncate" } }
      : { bottom: 0, itemWidth: 12, itemHeight: 8, itemGap: 12, textStyle: { fontSize: 11 } },
    series: [
      {
        type: "pie",
        radius: legend === "right" ? ["58%", "82%"] : ["52%", "74%"],
        center: legend === "right" ? ["30%", "50%"] : ["50%", "42%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: "#fff", borderWidth: 2, borderRadius: 6 },
        label: { show: !!center, position: "center", formatter: center ?? "", fontSize: 14, fontWeight: 600, color: "#1a1a1a" },
        data,
      },
    ],
  };
  return <EChart option={option} height={height} />;
}
